import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal, Select } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { EnterpriseMember, EnterpriseSeniority } from 'models/Enterprises';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import RevertIcon from 'icons/Revert.icon';
import TrashIcon from 'icons/Trash.icon';
import { getUserFullName } from 'utils';

import './DeleteMemberSeniorityModal.styles.scss';

export type DeleteSeniority = {
  deleteEnterpriseMemberIds?: string[];
  updateEnterpriseMembers?: {
    memberId: string;
    enterpriseSeniorityId: string;
  }[];
};

const INITIAL_DATA_FOR_SUBMIT: DeleteSeniority = {
  deleteEnterpriseMemberIds: [],
  updateEnterpriseMembers: [],
};

type DeleteMemberSeniorityModalProps = {
  className?: string;
  enterpriseSeniorityId: string;
  enterpriseMembersWithSeniorities: EnterpriseMember[];
  memberSeniorities: EnterpriseSeniority[];
  updateMemberSeniorities: (
    updatedMemberSeniorities: EnterpriseSeniority[],
    shouldRefetch?: boolean,
  ) => void;
} & ModalProps;

const DeleteMemberSeniorityModal: React.ForwardRefRenderFunction<
  ModalRef,
  DeleteMemberSeniorityModalProps
> = (props, ref) => {
  const {
    className,
    enterpriseSeniorityId,
    memberSeniorities,
    enterpriseMembersWithSeniorities = [],
    updateMemberSeniorities,
    onClose,
    ...rest
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [dataForSubmit, setDataForSubmit] = useState<DeleteSeniority>(
    INITIAL_DATA_FOR_SUBMIT,
  );

  const { popServerError } = useContext(PopNotificationsContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('delete-member-seniority-modal', className);

  const { t } = useTranslation();

  const validForSubmit = useMemo(
    () =>
      enterpriseMembersWithSeniorities.every(({ _id }) => {
        const { deleteEnterpriseMemberIds, updateEnterpriseMembers } =
          dataForSubmit;

        return (
          deleteEnterpriseMemberIds.includes(_id) ||
          updateEnterpriseMembers.some(({ memberId }) => memberId === _id)
        );
      }),
    [dataForSubmit, enterpriseMembersWithSeniorities],
  );

  const selectOptions = useMemo(
    () =>
      memberSeniorities
        .filter(({ _id }) => _id !== enterpriseSeniorityId)
        .map(({ _id, name }) => ({ value: _id, label: name })),
    [enterpriseSeniorityId, memberSeniorities],
  );

  const getSelectValue = useCallback(
    (mId: string) =>
      dataForSubmit.updateEnterpriseMembers.find(
        ({ memberId }) => memberId === mId,
      )?.enterpriseSeniorityId || null,
    [dataForSubmit.updateEnterpriseMembers],
  );

  const getIsDeleted = useCallback(
    (mId: string) =>
      dataForSubmit.deleteEnterpriseMemberIds.some(
        (memberId) => memberId === mId,
      ),
    [dataForSubmit.deleteEnterpriseMemberIds],
  );

  const onSelectChange = useCallback(
    (memberId: string, seniorityId: string) => {
      setDataForSubmit(
        ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
          deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.filter(
            (id) => id !== memberId,
          ),
          updateEnterpriseMembers: [
            ...updateEnterpriseMembers.filter(
              ({ memberId: id }) => id !== memberId,
            ),
            { memberId, enterpriseSeniorityId: seniorityId },
          ],
        }),
      );
    },
    [],
  );

  const onRemove = useCallback((memberId: string) => {
    setDataForSubmit(
      ({ deleteEnterpriseMemberIds, updateEnterpriseMembers }) => ({
        deleteEnterpriseMemberIds: deleteEnterpriseMemberIds.some(
          (id) => id === memberId,
        )
          ? deleteEnterpriseMemberIds.filter((id) => id !== memberId)
          : [...deleteEnterpriseMemberIds, memberId],
        updateEnterpriseMembers: [
          ...updateEnterpriseMembers.filter(
            ({ memberId: id }) => id !== memberId,
          ),
        ],
      }),
    );
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await api.enterprises.deleteMemberSeniority(
        enterpriseSeniorityId,
        dataForSubmit,
      );
      updateMemberSeniorities(data);
      modal?.close();
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    dataForSubmit,
    enterpriseSeniorityId,
    modal,
    popServerError,
    updateMemberSeniorities,
  ]);

  const onCustomClose = useCallback(() => {
    setDataForSubmit(INITIAL_DATA_FOR_SUBMIT);
    onClose?.();
  }, [onClose]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('connectMembersAndSeniority')}
      onClose={onCustomClose}
      {...rest}
    >
      {isLoading && <Loader />}

      <p className="description">
        {t('connectMembersAndSeniorityDescription')}
      </p>

      <div className="members-section">
        {enterpriseMembersWithSeniorities.map(
          ({ _id, firstName, lastName }) => {
            const isDeleted = getIsDeleted(_id);

            return (
              <div key={_id}>
                <p
                  className={classNames({
                    removed: isDeleted,
                  })}
                >
                  {getUserFullName(firstName, lastName)}
                </p>

                <Select
                  renderAsPortal
                  portalClassName="select-value-options"
                  value={getSelectValue(_id)}
                  placeholder={t('choose')}
                  options={selectOptions}
                  onChange={(e: any) => onSelectChange(_id, e.value)}
                />

                <Button
                  variant="icon"
                  icon={isDeleted ? RevertIcon : TrashIcon}
                  className={classNames({
                    'warning-action-button': !isDeleted,
                  })}
                  onClick={() => onRemove(_id)}
                />
              </div>
            );
          },
        )}
      </div>

      <div className="actions">
        <Button variant="link" onClick={modal?.close}>
          {t('cancel')}
        </Button>

        <Button disabled={isLoading || !validForSubmit} onClick={onSubmit}>
          {t('confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default forwardRef(DeleteMemberSeniorityModal);
